import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "react-query";
import { ArrowLeft, Plus, Settings, Share2 } from "lucide-react";
import { dashboardAPI } from "../services/api";

const DashboardDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [showAddChart, setShowAddChart] = useState(false);

  const {
    data: dashboardData,
    isLoading,
    error,
  } = useQuery(["dashboard", id], () => dashboardAPI.getDashboard(id!), {
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Failed to load dashboard</div>
        <Link to="/dashboard" className="btn-primary">
          Back to Dashboards
        </Link>
      </div>
    );
  }

  const { dashboard, charts } = dashboardData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard"
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>

          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {dashboard.name}
            </h1>
            {dashboard.description && (
              <p className="text-gray-600 mt-1">{dashboard.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button className="btn-secondary flex items-center space-x-2">
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </button>

          <button className="btn-secondary flex items-center space-x-2">
            <Share2 className="h-4 w-4" />
            <span>Share</span>
          </button>

          <button
            onClick={() => setShowAddChart(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Chart</span>
          </button>
        </div>
      </div>

      {/* Dashboard Content */}
      {charts.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg
              className="h-12 w-12 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No charts yet
          </h3>
          <p className="text-gray-600 mb-6">
            Add your first chart to start visualizing your data
          </p>
          <button onClick={() => setShowAddChart(true)} className="btn-primary">
            Add Chart
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {charts.map((chart: any) => (
            <div key={chart.id} className="chart-container">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {chart.name}
                </h3>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {chart.type}
                  </span>
                </div>
              </div>

              <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <div className="text-sm">Chart placeholder</div>
                  <div className="text-xs mt-1">
                    Data source: {chart.data_source_name}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Chart Modal */}
      {showAddChart && (
        <AddChartModal
          dashboardId={id!}
          onClose={() => setShowAddChart(false)}
          onSuccess={() => {
            setShowAddChart(false);
            // Refresh dashboard data
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};

// Add Chart Modal Component
interface AddChartModalProps {
  dashboardId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const AddChartModal: React.FC<AddChartModalProps> = ({
  dashboardId,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    type: "bar",
    dataSourceId: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.dataSourceId) return;

    try {
      setIsLoading(true);
      // await chartAPI.createChart({ ...formData, dashboardId });
      onSuccess();
    } catch (error: any) {
      console.error("Failed to create chart:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Add New Chart
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Chart Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="input"
                placeholder="Enter chart name"
                required
              />
            </div>

            <div>
              <label className="label">Chart Type</label>
              <select
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
                className="input"
              >
                <option value="bar">Bar Chart</option>
                <option value="line">Line Chart</option>
                <option value="pie">Pie Chart</option>
                <option value="scatter">Scatter Plot</option>
                <option value="area">Area Chart</option>
                <option value="doughnut">Doughnut Chart</option>
              </select>
            </div>

            <div>
              <label className="label">Data Source</label>
              <select
                value={formData.dataSourceId}
                onChange={(e) =>
                  setFormData({ ...formData, dataSourceId: e.target.value })
                }
                className="input"
                required
              >
                <option value="">Select a data source</option>
                {/* Data sources would be loaded here */}
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={
                  isLoading || !formData.name.trim() || !formData.dataSourceId
                }
              >
                {isLoading ? "Creating..." : "Create Chart"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DashboardDetail;




